import React, { useEffect, useRef } from 'react';

// This is a temporary scratchpad, never part of a submitted answer. Normalized
// points preserve handwriting through orientation changes and card resizes.
export default function WritingDrawingCanvas({ label, clearToken = 0 }) {
  const canvasRef = useRef(null);
  const strokes = useRef([]);
  const pointer = useRef(null);
  const redraw = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!context) return;
    const width = canvas.clientWidth || 1;
    const height = canvas.clientHeight || 1;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 3;
    context.strokeStyle = '#d92828';
    context.fillStyle = '#d92828';
    context.lineCap = 'round';
    context.lineJoin = 'round';
    for (const stroke of strokes.current) {
      const first = stroke[0];
      context.beginPath();
      context.arc(first.x * width, first.y * height, 1.5, 0, Math.PI * 2);
      context.fill();
      context.beginPath();
      context.moveTo(first.x * width, first.y * height);
      for (const point of stroke.slice(1)) context.lineTo(point.x * width, point.y * height);
      context.stroke();
    }
  };
  useEffect(() => {
    redraw();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(redraw) : null;
    if (canvasRef.current) observer?.observe(canvasRef.current);
    window.addEventListener('resize', redraw);
    return () => { observer?.disconnect(); window.removeEventListener('resize', redraw); };
  }, []);
  useEffect(() => { strokes.current = []; pointer.current = null; redraw(); }, [clearToken]);
  const point = event => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / (bounds.width || 1))),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / (bounds.height || 1))) };
  };
  const end = event => {
    if (pointer.current !== event.pointerId) return;
    pointer.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return <canvas ref={canvasRef} className="writing-drawing-canvas" aria-label={label}
    onPointerDown={event => {
      if (pointer.current !== null || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault(); pointer.current = event.pointerId;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      strokes.current.push([point(event)]); redraw();
    }}
    onPointerMove={event => {
      if (pointer.current !== event.pointerId || !strokes.current.length) return;
      event.preventDefault(); strokes.current[strokes.current.length - 1].push(point(event)); redraw();
    }} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end} />;
}
